'use client'

import { useTranslate } from '@tolgee/react'
import { MouseEvent, ReactNode, useCallback } from 'react'

import useProductImageState from '@/hooks/useProductImageState'
import useTracker from '@/hooks/useTracker'
import { toast } from 'oddle-shop-monorepo.design-system'

import AddToCartToast from '@/components/common/AddToCartToast'
import { useModal } from '@/contexts/ModalContext'
import { useOrderSetup } from '@/contexts/OrderSetupContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import { selectMenuMode } from '@/redux/meta/selector'
import { selectStoreOrder } from '@/redux/order'
import { useAddItemToCartMutation } from '@/services/local'
import { productStatus } from '@/utils/product'
import { useSelector } from 'react-redux'
import ProductGridCard from './ProductGridCard'
import ProductListCard from './ProductListCard'
import { usePreviewNavigationContext } from '@/contexts/PreviewNavigationProvider'
import { NextLink } from '@/libs/tolgee/navigation'
import useResponsive from '@/hooks/useResponsive'

interface ProductCardProps {
  showSideNav: boolean
  product: Oms.MenuItem
  category: Oms.MenuCategory
}

const ProductCard = ({
  showSideNav,
  product,
  category,
}: ProductCardProps): ReactNode => {
  const { t } = useTranslate()
  const { notifyAddToCart, notifyViewContent } = useTracker()
  const { showImage, showDefaultImage, productImageAspect, listView } =
    useProductImageState()
  const menuMode = useSelector(selectMenuMode)
  const orderSetup = useOrderSetup()
  const { openModal } = useModal()
  const [addItemToCartAction] = useAddItemToCartMutation()
  const storeOrder = useSelector(selectStoreOrder)
  const { replaceWithPreview } = usePreviewNavigationContext()
  const { mobile: isMobile } = useResponsive()

  const Card = listView ? ProductListCard : ProductGridCard

  const proceedWithAddToCart = useCallback(async () => {
    try {
      const variant = product.variants[0] as Oms.Variant
      const addItem = await addItemToCartAction({
        storeOrderId: storeOrder?.data?.id,
        orderItem: { variantId: variant.id, quantity: 1, subitems: [] },
      }).unwrap()
      if (addItem?.data?.payload?.storeOrder) {
        notifyAddToCart({
          menuItem: product,
          orderItem: { quantity: 1 },
          finalPrice: variant.price as number,
        })
        const items = [
          {
            name: product.name,
            quantity: 1,
            price: variant.price,
            imageUrl: product.imageFullUrls?.[0] || product.imageUrls?.[0],
          },
        ]
        if (!isMobile) {
          const toastId = toast(
            <AddToCartToast
              items={items}
              onClickGoCart={() => {
                toast.dismiss(toastId)
                openModal(MODAL_TYPES.CART_DRAWER)
              }}
            />,
            {
              position: 'top-right',
              duration: 2000,
              classNames: {
                toast: '!bg-container !top-[45px]',
                content: 'w-full',
              },
            }
          )
        }
      } else {
        toast.error(
          addItem?.data?.errorCode ||
            t('Cannot add item to cart. Please refresh browser and try again.')
        )
      }
    } catch (error) {
      toast.error(t('Cannot add item to cart on preview mode'))
      console.error(error)
    }
  }, [
    product,
    addItemToCartAction,
    storeOrder?.data?.id,
    notifyAddToCart,
    isMobile,
    openModal,
    t,
  ])

  const handleAddButtonClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      if (menuMode === 'preview') {
        toast.error(t('Cannot add item to cart on preview mode'))
        return
      }
      try {
        if (
          product.status === productStatus.AVAILABLE &&
          product.variants.length === 1 &&
          product.subitems.length === 0
        ) {
          if (!orderSetup.isOrderSetupComplete && !orderSetup.isForceFinished) {
            openModal(MODAL_TYPES.PICKUP_DELIVERY_SELECTION, {
              onFinished: proceedWithAddToCart,
            })
            return
          }
          await proceedWithAddToCart()
        } else {
          replaceWithPreview(`/?productId=${product.id}`, { scroll: false })
          notifyViewContent(product.id)
        }
      } catch (error) {
        toast.error(t('Cannot add item to cart on preview mode'))
        console.error(error)
      }
    },
    [
      product,
      t,
      orderSetup,
      menuMode,
      openModal,
      proceedWithAddToCart,
      replaceWithPreview,
      notifyViewContent,
    ]
  )

  const showProductDetailsModal = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      replaceWithPreview(`/?productId=${product.id}`, { scroll: false })
      notifyViewContent(product.id)
      return
    },
    [replaceWithPreview, notifyViewContent, product]
  )

  // Calculate column width based on view type
  const getColumnWidth = () => {
    if (listView) {
      return 'w-full lg:w-1/2'
    }
    if (showSideNav) {
      return 'lg:w-1/3'
    }
    return 'w-1/2 md:w-1/3 lg:w-1/4'
  }

  return (
    <div className={`w-1/2 ${getColumnWidth()} p-1 md:p-3`}>
      <NextLink
        href={`/?productId=${product.id}`}
        onClick={showProductDetailsModal}
        className="block h-full w-full"
      >
        <Card
          product={product}
          category={category}
          showImage={showImage}
          showDefaultImage={showDefaultImage}
          productImageAspect={productImageAspect}
          onButtonClick={handleAddButtonClick}
        />
      </NextLink>
    </div>
  )
}

export default ProductCard
